import React, { useState, useCallback } from 'react';

import noop from 'lodash/noop';

import { l10n } from '@ott/l10n';
import { hooks as virtualCardHooks } from '@ott/shared-virtual-card-logic';

import ErrorModal from '../ErrorModal';
import SuccessModal from '../SuccessModal';
import UpgradeFormModal from '../UpgradeFormModal';
import type { UpgradeParams } from '../UpgradeFormModal';
import { FormData } from '../UpgradeFormModal/types';
import ReleaseFormModal from './blocks/ReleaseFormModal';
import ReleasePromoModal from './blocks/ReleasePromoModal';
import { ReleaseErrors } from './helpers/constants';
import { getErrorModalContent } from './helpers/getErrorModalContent';
import { KMProvider, useKM } from './hooks/useKM';
import { ErrorResponse } from './types';

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardHooks;

type Props = {
  query?: Record<string, string>;
  kmParams?: Record<string, string>;
  withPromo?: boolean;
  withQuickAuthorization?: boolean;
  successContentOptions?: Record<string, any>;
  preventUserCloseModal?: boolean;
  onClose: VoidFunction;
  onRefill?: VoidFunction;
  onFormModalOpen?: VoidFunction;
  onFormSuccess?: VoidFunction;
  onFormError?: VoidFunction;
  onShowPromoModal?: VoidFunction;
};

const ReleaseModal = (props: Props) => {
  const {
    kmParams,
    withPromo = true,
    withQuickAuthorization = false,
    onClose,
    onFormModalOpen = noop,
    onFormSuccess = noop,
    onFormError = noop,
    onShowPromoModal = noop,
    preventUserCloseModal = false,
    successContentOptions = {},
  } = props;

  const { sendKM } = useKM();
  const { openRefillModal } = useVirtualCardFlow();

  const [isPromoShown, setIsPromoShown] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [upgradePayload, setUpgradePayload] = useState<UpgradeParams | null>(null);
  const showPromoModal = withPromo && !isPromoShown;

  const handleFormModalOpen = useCallback(() => {
    sendKM('Virtual_Card_Features_Confirm_No_UPRID_Press');
    onFormModalOpen();
    setIsPromoShown(true);
  }, []);

  const handleSuccessModalAction = useCallback(() => {
    sendKM('Virtual_Card_Issued_Success_Replenish_Balance_No_Uprid_Press');

    openRefillModal({
      kmParams,
    });
  }, [openRefillModal, kmParams]);

  const handleSuccessModalClose = useCallback(() => {
    sendKM('Virtual_Card_Issued_Success_Close_No_Uprid_Press');
    onClose();
  }, [onClose]);

  const handleFormSuccess = useCallback(() => {
    sendKM('Virtual_Card_Issued_Success_No_Uprid');

    onFormSuccess();
    setIsSuccess(true);
  }, []);

  const handleFormError = useCallback((error: ErrorResponse) => {
    const { error: errorText = '' } = error ?? {};
    if (errorText !== ReleaseErrors.invalidPin) {
      setError(errorText);
      setIsError(true);
    }
  }, []);

  const handleUpgradeFormSubmit = useCallback((data: FormData) => {
    const { taxOk, ...updateFormData } = data;

    if (!taxOk) {
      setIsError(true);
      setError(ReleaseErrors.taxNotOk);

      return;
    }

    const params = {
      ...updateFormData,
      isTaxResidentOfRussianFed: taxOk,
      isTaxResidentOfOtherCountries: !taxOk,
      wasBornInUnitedStates: !taxOk,
      documentSeries: '',
    };

    setUpgradePayload(params);
  }, []);

  const showUpgradForm = !upgradePayload;

  if (showPromoModal) {
    return (
      <ReleasePromoModal
        preventUserCloseModal={preventUserCloseModal}
        onAction={handleFormModalOpen}
        onClose={onClose}
        onShowPromoModal={onShowPromoModal}
      />
    );
  }

  if (isError) {
    const { actionButtonText, description, title, icon } = getErrorModalContent(error);

    return (
      <ErrorModal
        preventUserCloseModal={preventUserCloseModal}
        title={title}
        icon={icon}
        description={description}
        actionButtonText={actionButtonText}
        onClose={onClose}
        onAction={onFormError || onClose}
      />
    );
  }

  if (isSuccess) {
    return (
      <SuccessModal
        preventUserCloseModal={preventUserCloseModal}
        title={l10n('virtualCard.releaseSuccess.title')}
        description={l10n('virtualCard.releaseSuccess.description')}
        actionButtonText={l10n('virtualCard.releaseSuccess.action')}
        fullScreenOnMobile={true}
        onAction={handleSuccessModalAction}
        onClose={handleSuccessModalClose}
        customContentOptions={successContentOptions}
      />
    );
  }

  if (showUpgradForm) {
    return (
      <UpgradeFormModal
        preventUserCloseModal={preventUserCloseModal}
        onClose={onClose}
        onSubmit={handleUpgradeFormSubmit}
        title={l10n('virtualCard.releaseForm.title')}
      />
    );
  }

  return (
    <ReleaseFormModal
      preventUserCloseModal={preventUserCloseModal}
      onSuccess={handleFormSuccess}
      onError={handleFormError}
      onClose={onClose}
      withQuickAuthorization={withQuickAuthorization}
      upgradePayload={upgradePayload}
    />
  );
};

const Wrapper = (props: Props) => {
  const { kmParams, query } = props;
  return (
    <KMProvider kmParams={kmParams} query={query}>
      <ReleaseModal {...props} />
    </KMProvider>
  );
};

export default React.memo(Wrapper);
